// Form validation eGifts
import { number, object, string, ref } from 'yup'
import { unitedStates, states } from '../utility'
import {
    carrierMessageRegex,
    customerNameRegex,
    noSurroundingWhitespaceRegex,
    poBoxRegex,
    shippingNameRegex,
    shippingNameStartingLetterRegex,
    specialTestRegex,
    addressLineRegex,
} from '../utils/validationRegexes'
import {
    EMBOSS_MAX_LENGTH,
    EMBOSS_MIN_LENGTH,
    STANDARD_CARRIER_TO_MAX_LENGTH,
} from './formValidationSchemaValues'
import isEmpty from 'lodash/isEmpty'

// const websiteRegex =
//     /^(http:\/\/www\.|https:\/\/www\.|http:\/\/|https:\/\/)?[a-z0-9]+([-.]{1}[a-z0-9]+)*\.[a-z]{2,5}(:[0-9]{1,5})?(\/.*)?$/

const emailSchema = object().shape({
    email: string().email().required().nullable(true).label('Email'),
})

const plasticRecipientSchema = object().shape({
    shippingName: string()
        .min(2)
        .max(35)
        .test(
            'shippingName',
            'Only alpha characters, hyphens, and apostrophes are allowed',
            (val) => specialTestRegex.test(val)
        )
        .label('Shipping Name'),
    shippingLine1: string()
        .required()
        .max(50)
        .test(
            'addressLine1',
            'Only alphanumeric characters and hyphens are allowed',
            (val) => addressRegex.test(val)
        )
        .test(
            'poBox',
            'For security purposes, we are unable to ship to P.O. Boxes. We apologize for the inconvenience.',
            (val) => !poBoxRegex.test(val)
        )
        .label('Address Line 1'),
    shippingLine2: string().max(50).label('Address Line 2'),
    shippingCity: string()
        .required()
        .matches(
            /^[a-zA-Z0-9- ]+$/,
            'Only alphanumeric characters and hyphens are allowed'
        )
        .test(
            'city',
            'City must contain at least one alphanumeric character',
            (val) => noSurroundingWhitespaceRegex.test(val)
        )
        .label('City'),
    shippingState: string()
        .required()
        .test('len', 'Must be exactly 2 characters', (val) => val.length === 2)
        .test('match', 'Invalid abbreviation entered', (val) =>
            Object.values(unitedStates).includes(val)
        )
        .label('State'),
    shippingZip: string()
        .min(5)
        .max(5)
        .test('len', 'Must be exactly 5 characters', (val) => val.length === 5)
        .test('zipCode', 'Invalid zip code', (val) =>
            /(^\d{5}$)|(^\d{5}-\d{4}$)/.test(val)
        )
        .label('Zip'),
    shippingMethod: string()
        .required()
        .min(1, 'Shipping Method is a required field')
        .label('Shipping Method'),
})

// Form validation single eGift recipient
const eGiftRecipientSchema = object().shape({
    email: string().email().required().label('Email'),
    confirmEmail: string()
        .required()
        .oneOf([ref('email')], 'Email addresses do not match')
        .label('Confirm Email'),
    name: string().required().min(2).max(30).label('Name'),
    message: string().required().min(2).max(150).label('Message'),
})

// Form validation single visa virtual recipient
const visaVirtualRecipientSchema = object().shape({
    email: string()
        .email()
        .required()
        .max(255)
        .test(
            'email',
            'Email must contain at least one alphanumeric character',
            (val) => noSurroundingWhitespaceRegex.test(val)
        )
        .label('Recipient Email'),
    confirmEmail: string().oneOf(
        [ref('email')],
        'Email addresses do not match'
    ),
    name: string()
        .min(2)
        .max(50)
        .required()
        .test(
            'name',
            'Only alphanumeric characters and hyphens are allowed',
            (val) => noSurroundingWhitespaceRegex.test(val)
        )
        .label('Recipient Name'),
    message: string().required().min(2).max(250).label('Gift Message'),
    carrierFrom: string()
        .required()
        .min(2)
        .max(50)
        .test(
            'carrierFrom',
            'Only alphanumeric characters and hyphens are allowed',
            (val) => noSurroundingWhitespaceRegex.test(val)
        )
        .label('From'),
})

export const EMBOSS_LENGTH_MESSAGE = `Must be ${EMBOSS_MIN_LENGTH} to ${EMBOSS_MAX_LENGTH} characters long`
export const testEmboss = (val) => {
    return val.length >= EMBOSS_MIN_LENGTH && val.length <= EMBOSS_MAX_LENGTH
}

const openLoopPlasticSchema = (includeCarrierFields) => {
    return object().shape({
        recipientName: string()
            .test('len', EMBOSS_LENGTH_MESSAGE, (val) => {
                return testEmboss(val)
            })
            .test(
                'recipientName',
                'Only alphanumeric characters and hyphens are allowed',
                (val) => noSurroundingWhitespaceRegex.test(val)
            )
            .label('Recipient name'),
        embossMessage: string()
            .test('len', EMBOSS_LENGTH_MESSAGE, (val) => {
                return testEmboss(val)
            })
            .test(
                'embossMessage',
                'Only alphanumeric characters and hyphens are allowed',
                (val) => noSurroundingWhitespaceRegex.test(val)
            )
            .label('Message on card'),
        shippingName: string()
            .required()
            .min(2)
            .max(35)
            .test(
                'shippingName',
                'Shipping name must contain at least one alphanumeric character',
                (val) => shippingNameRegex.test(val)
            )
            .label('Shipping Name'),
        addressLine1: string()
            .required()
            .max(50)
            .test(
                'poBox',
                'For security purposes, we are unable to ship to P.O. Boxes. We apologize for the inconvenience.',
                (val) => !poBoxRegex.test(val)
            )
            .test(
                'addressLine1',
                'Address line 1 must contain at least one alphanumeric character',
                (val) => noSurroundingWhitespaceRegex.test(val)
            )
            .label('Address Line 1'),
        addressLine2: string().max(50).label('Address Line 2'),
        city: string()
            .required()
            .test(
                'city',
                'City must contain at least one alphanumeric character',
                (val) => noSurroundingWhitespaceRegex.test(val)
            )
            .label('City'),
        state: string()
            .test(
                'len',
                'Must be exactly 2 characters',
                (val) => val.length === 2
            )
            .test('match', 'Invalid abbreviation entered', (val) =>
                Object.values(unitedStates).includes(val)
            )
            .label('State'),
        zip: string()
            .required()
            .min(5)
            .max(5)
            .test(
                'len',
                'Must be exactly 5 characters',
                (val) => val.length === 5
            )
            .test('zipCode', 'Invalid zip code', (val) =>
                /(^\d{5}$)|(^\d{5}-\d{4}$)/.test(val)
            )
            .label('Zip'),
        shippingMethod: number()
            .required()
            .min(1, 'Shipping Method is a required field')
            .label('Shipping Method'),
        carrierFrom:
            includeCarrierFields &&
            string()
                .required()
                .min(2)
                .max(26)
                .test(
                    'carrierFrom',
                    'Only alphanumeric characters and hyphens are allowed',
                    (val) => noSurroundingWhitespaceRegex.test(val)
                )
                .label('From'),
        carrierMessage:
            includeCarrierFields &&
            string()
                .required()
                .min(2)
                .max(150)
                .test(
                    'carrierMessage',
                    'Only alphanumeric characters and hyphens are allowed',
                    (val) => noSurroundingWhitespaceRegex.test(val)
                )
                .label('Delivery message'),
    })
}

// Form validation single visa virtual recipient
const orderStatusFormSchema = object().shape({
    email: string().email().required().label('Email address'),
    orderNumber: string().min(10).required().label('Order number'),
})

// Form validation for SMB registration form
const smallBusinessRegistrationSchema = (newUser, fullSchema = true) => {
    return object().shape({
        companyName: string()
            .required()
            .max(100)
            .test('companyName', (val) =>
                noSurroundingWhitespaceRegex.test(val)
            )
            .label('Company name'),
        companyPhoneNumber: string()
            .required()
            .min(10)
            .max(10)
            .label('Company phone number'),
        addressLine1: string()
            .required()
            .min(3)
            .max(50)
            .label('Address line 1'),
        addressLine2: string().max(50).label('Address line 2'),
        city: string().required().max(50).label('City'),
        stateAbbr: string()
            .required()
            .test(
                'len',
                'Must be exactly 2 characters',
                (val) => val.length === 2
            )
            .test('match', 'Invalid abbreviation entered', (val) =>
                Object.values(unitedStates).includes(val)
            )
            .label('State'),
        zipCode: string()
            .test(
                'len',
                'Must be exactly 5 characters',
                (val) => val.length === 5
            )
            .test('zipCode', 'Invalid zip code', (val) =>
                /(^\d{5}$)|(^\d{5}-\d{4}$)/.test(val)
            )
            .label('Zip code'),
        contactName:
            newUser &&
            string()
                .max(50)
                .test('contactName', 'Provide contact full name', (val) =>
                    /^([a-zA-Z-]{2,}\s[a-zA-Z]{1,}\s?'?-?[a-zA-Z]{2,}\s?([a-zA-Z]{1,})?)/.test(
                        val
                    )
                )
                .label('Contact first and last name'),
        contactNumber:
            fullSchema &&
            string().required().min(10).max(10).label('Primary contact number'),
        contactEmail:
            fullSchema &&
            newUser &&
            string()
                .email()
                .required()
                .label('Primary contact business email address'),
        password: newUser && string().required().label('Password'),
        confirmedPassword:
            newUser &&
            string()
                .required()
                .oneOf([ref('password')], 'Passwords do not match')
                .label('Confirm password'),
        website: string().required(),
        ein: string().test('len', 'EIN must be 9 characters', function () {
            return this.parent.ein.length === 9
        }),
        sellCardsFlag: string().required().label('Sell cards selection'),
        reasonForInterest: string()
            .required()
            .max(50)
            .label('Reason for interest'),
    })
}
const smbBillingSchema = object().shape({
    name: string()
        .required()
        .min(2)
        .max(30)
        .test(
            'name',
            'Only alpha characters, hyphens, and apostrophes are allowed',
            (val) => specialTestRegex.test(val)
        )
        .label('Name'),
    address1: string().required().min(2).label('Address 1'),
    address2: string().label('Address 2'),
    city: string().required().min(2).label('City'),
    state: string()
        .required()
        .test('len', 'Must be exactly 2 characters', (val) => val.length === 2)
        .test('match', 'Invalid abbreviation entered', (val) =>
            Object.values(unitedStates).includes(val)
        )
        .label('State'),
    zip: string()
        .test('len', 'Must be exactly 5 characters', (val) => val.length === 5)
        .test('zip', 'Invalid zip code', (val) =>
            /(^\d{5}$)|(^\d{5}-\d{4}$)/.test(val)
        )
        .label('Zip'),
})

const forterSmbBillingSchema = object().shape({
    address1: string().required().label('Address 1'),
    address2: string().label('Address 2'),
    city: string().required().label('City'),
    state: string()
        .required()
        .test('len', 'Must be exactly 2 characters', (val) => val.length === 2)
        .test('match', 'Invalid abbreviation entered', (val) =>
            Object.values(unitedStates).includes(val)
        )
        .label('State'),
    zip: string()
        .test('len', 'Must be exactly 5 characters', (val) => val.length === 5)
        .test('zip', 'Invalid zip code', (val) =>
            /(^\d{5}$)|(^\d{5}-\d{4}$)/.test(val)
        )
        .label('Zip'),
})

const billingSchema = object().shape({
    name: string()
        .required()
        .min(2)
        .max(30)
        .test(
            'name',
            'Only alpha characters, hyphens, and apostrophes are allowed',
            (val) => specialTestRegex.test(val)
        )
        .test('format', 'Invalid name format', (val, testContext) => {
            if (!customerNameRegex.test(val)) {
                return testContext.createError({
                    path: 'name',
                    message: 'Please enter first and last name',
                })
            }
            const nameParts = val.split(' ')
            if (nameParts[0].length < 2 || nameParts[1].length < 2) {
                return testContext.createError({
                    path: 'name',
                    message:
                        'Please enter at least two characters (each field)',
                })
            }
            return true
        })
        .label('Name'),
    address1: string().required().label('Address 1'),
    address2: string().label('Address 2'),
    city: string().required().label('City'),
    state: string()
        .required()
        .test('len', 'Must be exactly 2 characters', (val) => val.length === 2)
        .test('match', 'Invalid value entered', (val) =>
            Object.values(unitedStates).includes(val)
        )
        .label('State'),
    zip: string()
        .test('len', 'Must be exactly 5 characters', (val) => val.length === 5)
        .test('zip', 'Invalid zip code', (val) =>
            /(^\d{5}$)|(^\d{5}-\d{4}$)/.test(val)
        )
        .label('Zip'),
})

const forterBillingSchema = object().shape({
    address1: string().required().label('Address 1'),
    address2: string().label('Address 2'),
    city: string().required().label('City'),
    state: string()
        .required()
        .test('len', 'Must be exactly 2 characters', (val) => val.length === 2)
        .test('match', 'Invalid abbreviation entered', (val) =>
            Object.values(unitedStates).includes(val)
        )
        .label('State'),
    zip: string()
        .test('len', 'Must be exactly 5 characters', (val) => val.length === 5)
        .test('zip', 'Invalid zip code', (val) =>
            /(^\d{5}$)|(^\d{5}-\d{4}$)/.test(val)
        )
        .label('Zip'),
    phone: string()
        .matches(
            /^\(?(\d{3})\)?[- ]?(\d{3})[- ]?(\d{4})$/,
            'Invalid phone number'
        )
        .required()
        .label('Phone'),
})

const fiservBillingSchema = object().shape({
    phone: string()
        .matches(
            /^\(?(\d{3})\)?[- ]?(\d{3})[- ]?(\d{4})$/,
            'Invalid phone number'
        )
        .required()
        .label('Phone'),
    // address1: string()
    //     .required()
    //     .max(50)
    //     .min(2)
    //     .test(
    //         'addressLine1',
    //         'Only alphanumeric characters and hyphens are allowed',
    //         (val) => addressLineRegex.test(val)
    //     )
    //     .label('Address 1'),
    // city: string()
    //     .required()
    //     .max(50)
    //     .min(2)
    //     .test(
    //         'city',
    //         'Only alphanumeric characters and hyphens are allowed',
    //         (val) => noSurroundingWhitespaceRegex.test(val)
    //     )
    //     .label('City'),
    // region: string()
    //     .required()
    //     .test('len', 'Must be exactly 2 characters', (val) => val.length === 2)
    //     .test('match', 'Invalid value entered', (val) =>
    //         Object.values(unitedStates).includes(val)
    //     )
    //     .label('State'),
    // postalCode: string()
    //     .required()
    //     .min(5)
    //     .max(5)
    //     .test('len', 'Must be exactly 5 characters', (val) => val.length === 5)
    //     .test('zipCode', 'Invalid zip code', (val) =>
    //         /(^\d{5}$)|(^\d{5}-\d{4}$)/.test(val)
    //     )
    //     .label('Zip'),
})

const guestCheckoutBillingSchema = object().shape({
    name: string()
        .required()
        .min(2)
        .max(30)
        .test('format', 'Invalid name format', (val, testContext) => {
            if (!specialTestRegex.test(val)) {
                return testContext.createError({
                    path: 'name',
                    message:
                        'Only alpha characters, hyphens, and apostrophes allowed',
                })
            }

            const nameParts = val.split(' ')
            if (nameParts[0].length < 2 || nameParts[1].length < 2) {
                //if less than 2 words return error message
                return testContext.createError({
                    path: 'name',
                    message: 'Please enter first and last name',
                })
            }
            return true
        })
        .label('Name'),
    address1: string().required().label('Address 1'),
    address2: string().label('Address 2'),
    city: string().required().label('City'),
    state: string()
        .required()
        .test('len', 'Must be exactly 2 characters', (val) => val.length === 2)
        .test('match', 'Invalid value entered', (val) =>
            Object.values(unitedStates).includes(val)
        )
        .label('State'),
    zip: string()
        .test('len', 'Must be exactly 5 characters', (val) => val.length === 5)
        .test('zip', 'Invalid zip code', (val) =>
            /(^\d{5}$)|(^\d{5}-\d{4}$)/.test(val)
        )
        .label('Zip'),
    email: string().email().required().label('Email'),
})

const forterGuestCheckoutBillingSchema = object().shape({
    address1: string().required().label('Address 1'),
    address2: string().label('Address 2'),
    city: string().required().label('City'),
    state: string()
        .required()
        .test('len', 'Must be exactly 2 characters', (val) => val.length === 2)
        .test('match', 'Invalid abbreviation entered', (val) =>
            Object.values(unitedStates).includes(val)
        )
        .label('State'),
    zip: string()
        .test('len', 'Must be exactly 5 characters', (val) => val.length === 5)
        .test('zip', 'Invalid zip code', (val) =>
            /(^\d{5}$)|(^\d{5}-\d{4}$)/.test(val)
        )
        .label('Zip'),
    email: string().email().required().label('Email'),
    phone: string()
        .matches(
            /^\(?(\d{3})\)?[- ]?(\d{3})[- ]?(\d{4})$/,
            'Invalid phone number'
        )
        .required()
        .label('Phone'),
})

const fiservGuestCheckoutBillingSchema = object().shape({
    email: string().email().required().label('Email'),
    phone: string()
        .matches(
            /^\(?(\d{3})\)?[- ]?(\d{3})[- ]?(\d{4})$/,
            'Invalid phone number'
        )
        .required()
        .label('Phone'),
    // address1: string()
    //     .required()
    //     .max(50)
    //     .min(2)
    //     .test(
    //         'addressLine1',
    //         'Only alphanumeric characters and hyphens are allowed',
    //         (val) => addressLineRegex.test(val)
    //     )
    //     .label('Address 1'),
    // city: string()
    //     .required()
    //     .max(50)
    //     .min(2)
    //     .test(
    //         'city',
    //         'Only alphanumeric characters and hyphens are allowed',
    //         (val) => noSurroundingWhitespaceRegex.test(val)
    //     )
    //     .label('City'),
    // region: string()
    //     .required()
    //     .test('len', 'Must be exactly 2 characters', (val) => val.length === 2)
    //     .test('match', 'Invalid value entered', (val) =>
    //         Object.values(unitedStates).includes(val)
    //     )
    //     .label('State'),
    // postalCode: string()
    //     .required()
    //     .min(5)
    //     .max(5)
    //     .test('len', 'Must be exactly 5 characters', (val) => val.length === 5)
    //     .test('zipCode', 'Invalid zip code', (val) =>
    //         /(^\d{5}$)|(^\d{5}-\d{4}$)/.test(val)
    //     )
    //     .label('Zip'),
})

const refundRequestSchema = object().shape({
    reason: string().required().label('Refund reason'),
    note: string().required().label('Note'),
})

const signupSchema = object().shape({
    name: string()
        .test(
            'name',
            'Only alpha characters, hyphens, and apostrophes allowed',
            (val) => specialTestRegex.test(val)
        )
        .min(2)
        .max(50)
        .required()
        .label('First and last name'),
    email: string().email().required().label('Email'),
    password: string()
        .min(8)
        .max(50)
        .test(
            'password',
            'Password must contain at least eight characters, including one number, special character, capital letter, and lower case letter',
            (val) =>
                /^((?=.*\d)(?=.*[^A-Z^a-z^0-9])(?=.*[a-z])(?=.*[A-Z]).{8,32})/.test(
                    val
                )
        )
        .label('Password'),
})

const signinSchema = object().shape({
    email: string().email().required().label('Email'),
    password: string().required().label('Password'),
})

const createACardLandingFormSchema = (isVirtual) => {
    return object().shape({
        amount: number().required().min(10).max(500).label('Amount'),
        quantity: number().required().min(1).max(20).label('Quantity'),
        nameOnCard:
            !isVirtual &&
            string()
                .required()
                .test('len', EMBOSS_LENGTH_MESSAGE, (val) => {
                    if (val) return testEmboss(val)
                    return false
                })
                .test(
                    'recipientName',
                    'Only alpha characters, hyphens, and apostrophes are allowed',
                    (val) => {
                        if (val) return specialTestRegex.test(val)
                        return false
                    }
                )
                .label('Recipient name'),
        messageOnCard:
            !isVirtual &&
            string()
                .optional()
                .test(
                    'messageOnCard',
                    'Only alphanumeric characters and hyphens are allowed',
                    (val) => {
                        if (!val) {
                            return true
                        }
                        return /^[a-zA-Z0-9- ]+$/.test(val)
                    }
                )
                .test('len', EMBOSS_LENGTH_MESSAGE, (val) => {
                    if (!val) {
                        return true
                    }
                    return testEmboss(val)
                })
                .test(
                    'messageOnCard',
                    'Emboss message must contain at least one alphanumeric character',
                    (val) => {
                        if (!val) {
                            return true
                        }
                        return noSurroundingWhitespaceRegex.test(val)
                    }
                )
                .label('Message on card'),
    })
}

const createACardShippingFormSchema = () => {
    return object().shape({
        shippingName: string()
            .required()
            .max(35)
            .min(2)
            .test(
                'shippingName',
                'Only alpha characters, hyphens, and apostrophes allowed',
                (val) => specialTestRegex.test(val)
            )
            .label('Shipping Name'),
        addressLine1: string()
            .required()
            .max(50)
            .test(
                'poBox',
                'For security purposes, we are unable to ship to P.O. Boxes. We apologize for the inconvenience.',
                (val) => !poBoxRegex.test(val)
            )
            .test(
                'addressLine1',
                'Only alphanumeric characters and hyphens are allowed',
                (val) => addressLineRegex.test(val)
            )
            .label('Address Line 1'),
        addressLine2: string().optional().max(50).label('Address 2'),
        city: string()
            .required()
            .matches(
                /^[a-zA-Z0-9- ]+$/,
                'Only alphanumeric characters and hyphens are allowed'
            )
            .test(
                'city',
                'City must contain at least one alphanumeric character',
                (val) => noSurroundingWhitespaceRegex.test(val)
            )
            .label('City'),
        state: string()
            .required()
            .test(
                'len',
                'Must be exactly 2 characters',
                (val) => val.length === 2
            )
            .test('match', 'Invalid abbreviation entered', (val) =>
                Object.values(unitedStates).includes(val)
            )
            .label('State'),
        zip: string()
            .test(
                'len',
                'Must be exactly 5 characters',
                (val) => val.length === 5
            )
            .test('zip', 'Invalid zip code', (val) =>
                /(^\d{5}$)|(^\d{5}-\d{4}$)/.test(val)
            )
            .label('Zip'),
        shippingMethod: string().required().label('Shipping Method'),
    })
}

const createACardPersonalizationFormSchema = (
    isVirtual,
    validateContactInfo,
    includeCarrierFrom
) => {
    return object().shape({
        carrierTo:
            validateContactInfo &&
            string()
                .required()
                .max(STANDARD_CARRIER_TO_MAX_LENGTH)
                .min(2)
                .test(
                    'shippingName',
                    'Only alpha characters, hyphens, and apostrophes allowed',
                    (val) => specialTestRegex.test(val)
                )
                .label('Shipping Name'),
        carrierMessage: string()
            .required()
            .min(2)
            .max(150)
            .label('Delivery message'),
        email:
            isVirtual &&
            string().email().required().max(255).label('Email Address'),
        carrierType: string().max(255).label('Carrier Type'),
        carrierFrom:
            includeCarrierFrom &&
            validateContactInfo &&
            string()
                .required()
                .min(2)
                .max(35)
                .test(
                    'carrierFrom',
                    'Only alpha characters, hyphens, and apostrophes are allowed',
                    (val) => specialTestRegex.test(val)
                )
                .label('From'),
    })
}

const emailValidationSchema = () => {
    return object().shape({
        email: string().email().required().max(255).label('Email Address'),
    })
}

const passwordValidationSchema = (validateConfirmedPassword) => {
    return object().shape({
        password: string().required().label('Password'),
        confirmedPassword:
            validateConfirmedPassword &&
            string()
                .required()
                .oneOf([ref('password')], 'Passwords do not match')
                .label('Confirm password'),
    })
}

const activateCardSchema = (validateEmail) => {
    return object().shape({
        cardNumber: string()
            .required()
            .min(16)
            .max(16)
            .test('cardNumber', 'Card number can only contain numbers', (val) =>
                /(^\d+$)/.test(val)
            )
            .label('Card number'),
        cvv: string()
            .required()
            .min(3)
            .max(3)
            .test('cvv', 'CVV can only contain numbers', (val) =>
                /(^\d+$)/.test(val)
            )
            .label('CVV'),
        email:
            validateEmail &&
            string()
                .email()
                .max(255)
                .test(
                    'email',
                    'Email is required to signup for our newsletter, uncheck to opt out',
                    (val) => validateEmail && val.length > 0
                )
                .label('Email'),
    })
}

const checkBalanceSchema = () => {
    return object().shape({
        cardNumber: string()
            .required()
            .min(16)
            .max(16)
            .test('cardNumber', 'Card number can only contain numbers', (val) =>
                /(^\d+$)/.test(val)
            )
            .label('Card number'),
        cvv: string()
            .required()
            .min(3)
            .max(3)
            .test('cvv', 'CVV can only contain numbers', (val) =>
                /(^\d+$)/.test(val)
            )
            .label('CVV'),
    })
}

const newsletterSignupSchema = () => {
    return object().shape({
        email: string().email().required().max(255).label('Email'),
    })
}

const groupShipmentsModalSchema = () => {
    return object().shape({
        shippingName: string()
            .required()
            .min(2)
            .max(35)
            .test(
                'shippingName',
                'Shipping Name must contain at least one alphanumeric character',
                (val) => shippingNameRegex.test(val)
            )
            .label('Shipping Name'),
    })
}

const updateFailedEmailRecipientSchema = () => {
    return object().shape({
        email: string().email().required().max(255).label('New email'),
    })
}

const smbShipToCellFormSchema = () => {
    return object().shape({
        firstAndLastName: string().required().max(50).label('Name'),
        addressLine1: string().required().max(50).label('Address 1'),
        addressLine2: string().optional().max(50).label('Address 2 (optional)'),
        city: string().required().max(50).label('City'),
        state: object()
            .typeError('Must enter a state')
            .required()
            .test(
                'len',
                'Must be exactly 2 characters',
                (val) => !isEmpty(val) && val.code.length === 2
            )
            .test('match', 'Invalid abbreviation entered', (val) =>
                states.some((state) => state.code === val.code)
            )
            .label('State'),
        zip: string()
            .required()
            .test(
                'len',
                'Must be exactly 5 characters',
                (val) => !isEmpty(val) && val.length === 5
            )
            .test('zip', 'Invalid zip code', (val) =>
                /(^\d{5}$)|(^\d{5}-\d{4}$)/.test(val)
            )
            .label('Zip'),
    })
}

const virtualExchangeShippingSchema = () => {
    return object().shape({
        firstName: string()
            .required()
            .min(2)
            .max(25)
            .test(
                'firstName',
                'First name must contain at least two alphanumeric characters',
                (val) => shippingNameRegex.test(val)
            )
            .label('First Name'),
        lastName: string()
            .required()
            .min(2)
            .max(25)
            .test(
                'lastName',
                'Last name must contain at least two alphanumeric characters',
                (val) => shippingNameRegex.test(val)
            )
            .label('Last Name'),
        addressLine1: string().required().min(10).max(100).label('Address 1'),
        addressLine2: string()
            .optional()
            .max(100)
            .label('Address 2 (optional)'),
        city: string().required().max(50).label('City'),
        stateAbbreviation: string()
            .required()
            .test(
                'len',
                'Must be exactly 2 characters',
                (val) => !isEmpty(val) && val.length === 2
            )
            .test('match', 'Invalid abbreviation entered', (val) =>
                states.some((state) => state.code === val)
            )
            .label('State'),
        zipCode: string()
            .required()
            .test(
                'len',
                'Must be exactly 5 digits',
                (val) => !isEmpty(val) && val.length === 5
            )
            .test('zip', 'Invalid zip code', (val) => /(^\d{5}$)/.test(val))
            .label('Zip'),
    })
}
// helper for yup transform function
function emptyStringToNull(value, originalValue) {
    if (typeof originalValue === 'string' && originalValue === '') {
        return null
    }
    return value
}
const smbManualPlasticDetailsSchema = () => {
    return object().shape({
        quantity: number()
            .required()
            .min(1)
            .max(50)
            .transform(emptyStringToNull)
            .nullable()
            .label('Quantity (1-50)'),
        amount: number()
            .required(
                'Card value ($10-$500) must be greater than or equal to 10'
            )
            .min(10)
            .max(500)
            .transform(emptyStringToNull)
            .nullable()
            .label('Card value ($10-$500)'),
        nameOnCard: string()
            .required()
            .min(2)
            .max(20)
            .test(
                'nameOnCard',
                'Only alpha characters, hyphens, and apostrophes allowed',
                (val) => specialTestRegex.test(val)
            )
            .label('Line 1'),
        cardMessage: string()
            .required()
            .min(2)
            .max(20)
            .test(
                'cardMessage',
                'Only alpha characters, hyphens, and apostrophes allowed',
                (val) => specialTestRegex.test(val)
            )
            .label('Line 2'),
        carrierTo: string()
            .min(2)
            .max(26)
            .required()
            .test(
                'carrierTo',
                'Only alpha characters, hyphens, and apostrophes allowed',
                (val) => specialTestRegex.test(val)
            )
            .label('Recipient name on paper carrier (To:)'),

        carrierFrom: string()
            .required()
            .min(2)
            .max(26)
            .test(
                'carrierFrom',
                'Only alpha characters, hyphens, and apostrophes allowed',
                (val) => specialTestRegex.test(val)
            )
            .label('Sender name on paper carrier (From:)'),

        carrierMessage: string()
            .max(150)
            .test(
                'carrierMessage',
                "Only alphanumeric and special characters (!?-',.)  are allowed",
                (val) => carrierMessageRegex.test(val)
            )
            .label('Custom message on paper carrier'),
    })
}
const smbManualEGiftDetailsSchema = () => {
    return object().shape({
        quantity: number()
            .required()
            .min(1)
            .max(50)
            .transform(emptyStringToNull)
            .nullable()
            .label('Quantity'),
        amount: number()
            .required()
            .min(10)
            .max(500)
            .transform(emptyStringToNull)
            .nullable()
            .label('Amount'),
        firstName: string()
            .required()
            .min(2)
            .max(26)
            .test('firstName', (val) => noSurroundingWhitespaceRegex.test(val))
            .label('First Name'),
        lastName: string()
            .required()
            .min(2)
            .max(26)
            .test('lastName', (val) => noSurroundingWhitespaceRegex.test(val))
            .label('Last Name'),
        emailMessage: string()
            .required()
            .min(1)
            .max(150)
            .test(
                'emailMessage',
                "Only alphanumeric and special characters (!?-',.)  are allowed",
                (val) => {
                    if (val) return carrierMessageRegex.test(val)
                    return false
                }
            )
            .label('Email Message'),
        senderName: string()
            .required()
            .min(2)
            .max(26)
            .test('senderName', (val) => noSurroundingWhitespaceRegex.test(val))
            .label('From'),
    })
}

const smbExpressPlasticDetailsSchema = () => {
    return object().shape({
        quantity: number()
            .required()
            .min(5, 'A minimum of 5 cards per order')
            .max(
                50,
                'A maximum of 50 cards per order. Please contact our corporate sales team for greater quantities'
            )
            .label('Quantity (5-50)'),
        amount: number()
            .required()
            .min(10)
            .max(500)
            .label('Card value ($10-$500)'),
        messageOnCard: string()
            .optional()
            .min(2)
            .max(20)
            .test(
                'cardMessage',
                'Only alpha characters, hyphens, and apostrophes allowed',
                (val) => specialTestRegex.test(val)
            )
            .transform(emptyStringToNull)
            .nullable()
            .label('Custom message'),
    })
}

export {
    emailSchema,
    plasticRecipientSchema,
    eGiftRecipientSchema,
    openLoopPlasticSchema,
    visaVirtualRecipientSchema,
    orderStatusFormSchema,
    smallBusinessRegistrationSchema,
    smbBillingSchema,
    forterSmbBillingSchema,
    billingSchema,
    fiservBillingSchema,
    forterBillingSchema,
    guestCheckoutBillingSchema,
    forterGuestCheckoutBillingSchema,
    fiservGuestCheckoutBillingSchema,
    refundRequestSchema,
    signupSchema,
    signinSchema,
    createACardLandingFormSchema,
    createACardShippingFormSchema,
    emailValidationSchema,
    passwordValidationSchema,
    activateCardSchema,
    checkBalanceSchema,
    newsletterSignupSchema,
    groupShipmentsModalSchema,
    updateFailedEmailRecipientSchema,
    createACardPersonalizationFormSchema,
    smbShipToCellFormSchema,
    virtualExchangeShippingSchema,
    smbManualPlasticDetailsSchema,
    smbManualEGiftDetailsSchema,
    smbExpressPlasticDetailsSchema,
}
